import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PlusIcon } from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import {
  Button,
  SearchableSelect,
  Card,
  Badge,
  getStatusBadgeVariant,
  InsurerLogo,
  Pagination,
  CompactFilterBar,
  Table,
} from '@/components/common';
import { PolicyNumber, policyNumberLabel } from '@/components/common/PolicyNumber';
import type { Column } from '@/components/common/Table';
import { useClaims } from '@/hooks/useClaims';
import { useCan } from '@/hooks/useEmployeeRights';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import { useFilterParams } from '@/hooks/useFilterParams';
import { isInsurerRole } from '@/routing/portalHome';
import { useAuthStore } from '@/store';
import { formatDate, titleCase } from '@/utils';
import type { Claim, ClaimFilters, ClaimStatus } from '@/types';
import { ClaimsLimitButton, INSURER_QUEUE_TABS, InsurerQueueTabs } from './InsurerClaimsQueue';

const statusOptions = [
  { value: '', label: 'All Status' },
  { value: 'pending', label: 'Pending' },
  { value: 'draft', label: 'Draft' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'acknowledged', label: 'Acknowledged' },
  { value: 'under_investigation', label: 'Under Investigation' },
  { value: 'surveyor_assigned', label: 'Surveyor Assigned' },
  { value: 'survey_completed', label: 'Survey Completed' },
  { value: 'documents_pending', label: 'Documents Pending' },
  { value: 'under_review', label: 'Under Review' },
  { value: 'approved', label: 'Approved' },
  { value: 'partially_approved', label: 'Partially Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'settled', label: 'Settled' },
  { value: 'closed', label: 'Closed' },
];

/** The name shown in the Customer column: `customer_name`, else `holder_name`
 *  (all an insurer role is served), else a dash. */
function customerLabel(claim: Claim): string {
  return claim.customer_name || claim.holder_name || '—';
}

/** The values the Status control can legitimately hold. A restored `?f_status=`
 *  outside this set is DROPPED rather than sent: `status` is a
 *  `filterset_fields` entry on ClaimViewSet, and django-filter answers an
 *  unknown choice with a 400, so a hand-edited or stale URL would take the
 *  whole list down instead of simply being ignored. */
const STATUS_VALUES = new Set(statusOptions.map((option) => option.value).filter(Boolean));

/** The "Pending" pseudo-status: not a stored value at all, but the four
 *  in-flight ones a desk means by it. Sent as `status__in` (the service maps
 *  `status_in` onto that name), never as `status`. */
const PENDING_STATUSES = 'submitted,acknowledged,under_review,surveyor_assigned';

// NOTE: there is deliberately no priority filter. Priority is set by the broker
// on the claim record and is still shown as a badge on every row, but filtering
// the list by it was removed — it is not a way anyone triages claims here.

/**
 * Q-01 -- the filter state this page mirrors into the query string.
 *
 * DELIBERATELY NOT HERE: the page number. Every filter change resets it to 1
 * (and the debounced-search effect re-asserts that on mount), so a restored
 * page number would be clobbered before the first paint, leaving a URL that
 * says `page=3` over a list showing page 1.
 */
type ClaimPanelFilters = {
  search: string;
  /** The Status control's own value, INCLUDING the `pending` pseudo-status. */
  status: string;
};

const EMPTY_FILTERS: ClaimPanelFilters = {
  search: '',
  status: '',
};

/**
 * THE INCIDENT-DATE WINDOW IS NOT HERE, AND THAT IS THE FIX.
 *
 * There was a "From Date" control on this panel, and this sweep briefly added
 * the matching "Incident To" beside it. Both were removed again, because
 * NOTHING ON THE SERVER READS EITHER ONE:
 *
 *   `ClaimViewSet` (backend/apps/claims/views.py:111-114) has
 *   `filter_backends = [DjangoFilterBackend, SearchFilter, OrderingFilter]`
 *   with `filterset_fields = ["status", "claim_type"]` and NO
 *   `filterset_class`; there is no `apps/claims/filters.py`; and
 *   `get_queryset` hand-handles only `status__in` and `customer`. A grep of
 *   the whole backend for `incident_date_after` / `incident_date_before` --
 *   the names `services/claims.ts` put `date_from` / `date_to` on the wire as
 *   -- returns nothing. django-filter DROPS an unknown parameter silently, so
 *   the request came back completely unnarrowed.
 *
 * It was worse than inert: `activeFilterCount` counted the window, so picking
 * a January window badged the Filter button "1" and raised a Clear button over
 * a list identical to the unfiltered one. That is this project's signature
 * defect -- a control that reports success while doing nothing -- and adding
 * the second bound would have doubled it rather than fixed it.
 *
 * TO BRING IT BACK, the backend comes FIRST: give `ClaimViewSet` a
 * `filterset_class` exposing `incident_date` as a `DateFromToRangeFilter`
 * (which yields exactly `incident_date_after` / `incident_date_before`, so
 * `services/claims.ts` needs no change), or parse both parameters in
 * `get_queryset` beside the existing `status__in` block. `incident_date` is
 * already indexed on the model. Only then do the two `<Input type="date">`
 * controls, the `incident_date` key here and its share of
 * `activeFilterCount` go back in -- with a backend test that proves a window
 * excludes a claim outside it, not a frontend one that only asserts what the
 * client put in its own filters object.
 */

/**
 * Namespaced even though this page honours NO unprefixed deep-link params
 * today (nothing in the app links to `/claims?…` — only `/claims/:id` and
 * `/claims/new?policyId=`). Keeping the filters inside `f_` means a deep link
 * can be added later — `?status=`, `?customer=` are both already on the wire —
 * without the user's own choices colliding with the seed, which is the bug the
 * Policies pilot had to unpick twice.
 *
 * Consequence for this file: there is no `initial` seed and no
 * `consumeSeedParams`. `clearAll()` empties the whole namespace this page
 * owns, so Clear genuinely survives a refresh here. If an unprefixed deep link
 * is ever added, it MUST arrive together with `DEEP_LINK_PARAMS`,
 * `SEED_PARAMS_FOR` and a `consumeSeedParams` call in every handler that
 * overrides a seeded key.
 */
const FILTER_PREFIX = 'f_';

/**
 * U-04: the ONLY fields `ClaimViewSet.ordering_fields` accepts
 * (backend/apps/claims/views.py:114) --
 * `["created_at", "intimation_date", "claimed_amount"]`.
 *
 * NOT ONE OF THEM IS A COLUMN ON THIS PAGE. The five columns are the claim
 * number, the customer, the INCIDENT date, the priority and the status;
 * `incident_date` is indexed on the model but is absent from `ordering_fields`,
 * and `claimed_amount` is deliberately not displayed (pinned by
 * `ClaimsListPage.test.tsx`). So no header here is marked `sortable`.
 *
 * That is the honest outcome, not an oversight: DRF's OrderingFilter silently
 * DROPS a term it does not recognise and returns the default order, so a
 * sortable "Incident Date" header would render as a control, fire a request,
 * and come back in exactly the order it was already in. Making it real is a
 * one-line backend change (add `incident_date` to `ordering_fields`) which is
 * outside this lane -- see the handover.
 */
const SORTABLE_FIELDS = new Set(['created_at', 'intimation_date', 'claimed_amount']);

export function ClaimsListPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  // B4: raising a claim is claims Create (the /claims/new route checks it too).
  // #395: insurer roles are read-only on claims; the API refuses their create.
  const { user } = useAuthStore();
  const isInsurer = isInsurerRole(user?.role);
  const canCreateClaim = useCan('claims', 'create') && !isInsurer;
  // #401: the insurer Claims Desk queue tab. Replaces the Status control's
  // value while a tab other than All is chosen.
  const [queueTab, setQueueTab] = useState('all');
  const activeTab = isInsurer ? INSURER_QUEUE_TABS.find((tab) => tab.key === queueTab) : undefined;

  // Q-01: the panel's own filters, mirrored into `?f_*` so they survive a
  // refresh, a bookmark and a shared link. Restored SYNCHRONOUSLY in the
  // hook's lazy initializer, so the very first request is already filtered and
  // CompactFilterBar's panel is already open over it.
  const { filters, setFilters, clearAll } = useFilterParams<ClaimPanelFilters>({
    defaults: EMPTY_FILTERS,
    prefix: FILTER_PREFIX,
  });

  // Global rule 1 -- search-as-you-type without a request per keystroke:
  // `searchTerm` drives the Input synchronously, `debouncedTerm` (300ms) feeds
  // the filters. Seeded from the restored `?f_search=` so a refresh brings the
  // box back filled in, not just the results.
  const { term: searchTerm, debouncedTerm, setTerm: setSearchTerm } = useDebouncedSearch(filters.search);

  // A hand-edited or stale `?f_status=` must not 400 the list.
  const status = STATUS_VALUES.has(filters.status) ? filters.status : '';

  // The wire shape. Memoized because it is the react-query key: rebuilding it
  // every render would refetch the list on every unrelated re-render.
  const queryFilters = useMemo<ClaimFilters>(() => {
    const base: ClaimFilters = {
      search: filters.search || undefined,
      // "Pending" is four statuses, not one -- it goes out as `status__in`
      // INSTEAD of `status`, never alongside it.
      status: status && status !== 'pending' ? (status as ClaimStatus) : undefined,
      status_in: status === 'pending' ? PENDING_STATUSES : undefined,
      // No `date_from` / `date_to`: the server reads neither. See the comment
      // above EMPTY_FILTERS.
    };
    // #401: an insurer queue tab other than All decides the status filter.
    if (activeTab && activeTab.key !== 'all') {
      return { search: base.search, ...activeTab.filters };
    }
    return base;
  }, [filters.search, status, activeTab]);

  const { data, isLoading, isError, refetch } = useClaims(page, queryFilters);
  const claims = useMemo(() => data?.results ?? [], [data]);
  const totalItems = data?.count ?? 0;
  const itemsPerPage = 20;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Push the debounced term into the filters and reset to page 1 whenever the
  // effective search changes. Keyed off a ref of the LAST TERM PUSHED, not off
  // `filters.search`: seeded with the restored value it makes the mount pass a
  // no-op (no second request), and because Clear resets `filters.search` while
  // `debouncedTerm` still holds the old text for another 300ms, comparing
  // against `filters.search` would immediately re-apply the search just cleared.
  const pushedTermRef = useRef(filters.search);
  useEffect(() => {
    if (pushedTermRef.current === debouncedTerm) return;
    pushedTermRef.current = debouncedTerm;
    setFilters({ search: debouncedTerm });
    setPage(1);
  }, [debouncedTerm, setFilters]);

  const handleStatusFilterChange = (value: string) => {
    setFilters({ status: value });
    setPage(1);
  };

  // Only the PANEL's own controls -- which, since the inert incident-date
  // window came out, is just Status. The search box is always visible, so it is
  // excluded as everywhere else. NOTHING WHOSE PARAMETER THE SERVER IGNORES MAY
  // BE COUNTED HERE: a badge is a claim that the list in front of the user has
  // been narrowed.
  const activeFilterCount = [status].filter(Boolean).length;

  const clearFilters = () => {
    setSearchTerm('');
    clearAll();
    setPage(1);
  };

  const handleRowClick = (claim: Claim) => {
    navigate(`/claims/${claim.id}`);
  };

  const getPriorityBadgeVariant = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      default:
        return 'default';
    }
  };

  /**
   * U-02: the columns, with a width each.
   *
   * SIZE = CONTENT + CELL PADDING. `Table` pads every `<td>` with `px-6`
   * (48px) and, under `layout="fixed"`, sets `truncate` (`overflow: hidden`),
   * and cells are border-box -- so a column declared at 8rem offers 80px of
   * content, not 128px, and anything wider is CLIPPED with no scrollbar. The
   * three narrow columns therefore override the padding to `px-3` (24px)
   * through `className` (`cn` is tailwind-merge, so a `px-*` there beats the
   * `px-6`) and take their width from the arithmetic below:
   *
   *   claim          13rem  px-6 -> 160px  ("CLM-2026-0001" over a policy no.)
   *   customer       12rem  px-6 -> 144px
   *   insurer        12rem  px-6 -> 144px  (U-05: 20px logo + gap + name)
   *   incident_date   9rem  px-3 -> 120px  ("May 4, 2026": ~85px)
   *   priority        8rem  px-3 -> 104px  (the widest badge, "Medium": ~62px)
   *   status         10rem  px-3 -> 136px  ("Under Investigation": ~130px)
   *                 -------
   *                  64rem = 1024px, well inside the ~1424px a 1440-1536px
   *   desktop can show without a horizontal page scrollbar. Under `table-fixed`
   *   with `w-full` the surplus is shared out proportionally, so every column
   *   is at least as wide as declared -- the arithmetic is a floor, not a cap.
   *
   * THE THREE CELLS THAT RENDER JSX carry their OWN `title`: `Table` sets a
   * tooltip only when a cell renders to a string or a number (stringifying
   * arbitrary JSX would show "[object Object]"), so a truncated date or badge
   * would otherwise have nothing to hover. Both badges also get `max-w-full`
   * with the label in a `truncate` span -- an `inline-flex` badge is an atomic
   * inline-level box, which a `truncate` ancestor hard-CLIPS with no ellipsis.
   */
  // #636: the widths below sum to 67.5rem (the insurer view, with Desk), so the
  // Desk chip is not clipped at 1440px.
  const columns: Column<Claim>[] = [
    {
      key: 'claim',
      header: 'Claim',
      width: '13rem',
      render: (claim: Claim) => (
        <div>
          <p className="truncate text-sm font-medium text-gray-900 dark:text-white" title={claim.claim_number}>
            {claim.claim_number}
          </p>
          {/* Queue #241: same identifier rule as the Policies list -- the
              insurer's number, our POL- reference only when that is blank. */}
          <p
            className="truncate text-xs text-gray-500 dark:text-gray-400"
            title={policyNumberLabel(claim)}
          >
            <PolicyNumber source={claim} />
          </p>
        </div>
      ),
    },
    {
      key: 'customer',
      header: 'Customer',
      width: '11rem',
      render: (claim: Claim) => (
        <div>
          {/* Queue #239: the list payload had no `customer_name`, so this line
              rendered empty and the raw claim type below it read as the
              Customer column. */}
          {/* #395: insurer roles get a strict allow-list with `holder_name` but
              no `customer_name`; fall back to it rather than showing a dash. */}
          <p className="truncate text-sm text-gray-900 dark:text-white" title={customerLabel(claim)}>
            {customerLabel(claim)}
          </p>
          <p
            className="truncate text-xs text-gray-500 dark:text-gray-400"
            title={claim.claim_type_display || titleCase(claim.claim_type)}
          >
            {claim.claim_type_display || titleCase(claim.claim_type)}
          </p>
        </div>
      ),
    },
    {
      // U-05: the claim is against an insurer, and the list never said which.
      // Logo + name (the blurb, when there is one, rides in the logo's hover).
      key: 'insurer',
      header: 'Insurer',
      width: '10.5rem',
      render: (claim: Claim) => (
        <span className="flex items-center gap-1.5" title={claim.insurer_name || '—'}>
          <InsurerLogo
            src={claim.insurer_logo_url}
            name={claim.insurer_name}
            description={claim.insurer_description}
            size="xs"
          />
          <span className="min-w-0 truncate text-sm text-gray-900 dark:text-white">
            {claim.insurer_name || '—'}
          </span>
        </span>
      ),
    },
    {
      key: 'incident_date',
      header: 'Incident Date',
      width: '8.5rem',
      className: 'px-3',
      // Evaluates to FALSE: `incident_date` is not in ClaimViewSet.ordering_fields.
      // Written as the lookup rather than a literal so the day the backend
      // gains the field this header becomes real instead of staying a comment.
      sortable: SORTABLE_FIELDS.has('incident_date'),
      render: (claim: Claim) => (
        <p
          className="truncate text-sm text-gray-500 dark:text-gray-400"
          title={formatDate(claim.incident_date)}
        >
          {formatDate(claim.incident_date)}
        </p>
      ),
    },
    {
      key: 'priority',
      header: 'Priority',
      width: '7rem',
      className: 'px-3',
      render: (claim: Claim) => (
        <Badge variant={getPriorityBadgeVariant(claim.priority)} size="sm" className="max-w-full">
          <span className="truncate" title={titleCase(claim.priority)}>
            {titleCase(claim.priority)}
          </span>
        </Badge>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '9rem',
      className: 'px-3',
      render: (claim: Claim) => (
        <Badge variant={getStatusBadgeVariant(claim.status)} size="sm" className="max-w-full">
          <span className="truncate" title={titleCase(claim.status)}>
            {titleCase(claim.status)}
          </span>
        </Badge>
      ),
    },
    // #401: the insurer's queue also shows the age on its desk and how many
    // documents it is still waiting for.
    ...(isInsurer
      ? [
          {
            key: 'desk',
            header: 'Desk',
            width: '8.5rem',
            className: 'px-3',
            render: (claim: Claim) => <InsurerDeskCell claim={claim} />,
          },
        ]
      : []),
  ];

  return (
    <div>
      <PageHeader
        title="Claims"
        subtitle={`${totalItems} total claims`}
        breadcrumbs={[{ label: 'Claims' }]}
        actions={
          canCreateClaim ? (
            <Link to="/claims/new">
              <Button leftIcon={<PlusIcon className="w-5 h-5" />}>New Claim</Button>
            </Link>
          ) : user?.role === 'insurer_admin' ? (
            <ClaimsLimitButton />
          ) : undefined
        }
      />

      {isInsurer && (
        <InsurerQueueTabs
          active={queueTab}
          onChange={(key) => {
            setQueueTab(key);
            setPage(1);
          }}
        />
      )}

      {/* One line by default -- search box + Filter toggle badged with the
          active count. The panel starts expanded whenever a filter is already
          set (restored from `?f_*` after a refresh), so a filtered list is
          never painted with nothing on screen to explain it. */}
      <CompactFilterBar
        search={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search by claim number, policy, or customer..."
        activeFilterCount={activeFilterCount}
        onClearFilters={clearFilters}
      >
        <SearchableSelect
          id="claim-status-filter"
          label="Status"
          options={statusOptions}
          value={status}
          onChange={handleStatusFilterChange}
          placeholder="Status"
        />
        {/* No incident-date window: the server reads neither bound. See the
            comment above EMPTY_FILTERS for what it would take to offer one. */}
      </CompactFilterBar>

      {/* A REFRESH that failed while the rows it was replacing are still in
          hand -- react-query keeps the previous page's data (`placeholderData`)
          on a refetch of the same key, so without this the list would look
          current while the server had stopped answering. */}
      {isError && claims.length > 0 && (
        <div
          role="alert"
          className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300"
        >
          <span>Could not refresh this list. These are the results from before your last change.</span>
          <button type="button" className="font-medium underline" onClick={() => void refetch()}>
            Try again
          </button>
        </div>
      )}

      {/* Three states, never two: a failed request, an empty book and a loading
          list are different facts and each has its own branch. This page used
          to have TWO -- a failed claims request rendered "No claims found",
          telling a broker there was nothing to see when the server had simply
          refused the request. */}
      {isLoading ? (
        <Table<Claim> data={[]} columns={columns} isLoading rowKey={(claim) => claim.id} />
      ) : isError && claims.length === 0 ? (
        <Card padding="none">
          <div role="alert" className="px-6 py-12 text-center">
            <p className="text-sm font-medium text-red-700 dark:text-red-400">Could not load claims.</p>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              That is not the same as there being none — please try again.
            </p>
            <button
              type="button"
              className="mt-3 text-sm text-primary-600 underline hover:no-underline dark:text-primary-400"
              onClick={() => void refetch()}
            >
              Try again
            </button>
          </div>
        </Card>
      ) : claims.length === 0 ? (
        <Card padding="none">
          <EmptyState />
        </Card>
      ) : (
        <>
          <Table<Claim>
            data={claims}
            columns={columns}
            rowKey={(claim) => claim.id}
            onRowClick={handleRowClick}
            // U-02: declared widths (no column jump when the filters change
            // which rows are on screen) and a header that stays put while the
            // rows scroll under it.
            layout="fixed"
            stickyHeader
            // U-04: no `onSortChange`. Without it `Table` renders every header
            // as plain text however many columns claim to be sortable -- which
            // is the correct outcome while the server can order by none of
            // them. See SORTABLE_FIELDS above.
          />
          {totalPages > 1 && (
            <Card padding="none" className="mt-4 overflow-hidden">
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={setPage}
              />
            </Card>
          )}
        </>
      )}
    </div>
  );
}

/** #401: days on the insurer's desk and open document requests. */
function InsurerDeskCell({ claim }: { claim: Claim }) {
  const desk = claim as Claim & { forwarded_to_insurer_at?: string | null; open_document_requests_count?: number };
  const days = desk.forwarded_to_insurer_at
    ? Math.max(0, Math.floor((Date.now() - new Date(desk.forwarded_to_insurer_at).getTime()) / 86_400_000))
    : null;
  return (
    <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300">
      <span>{days === null ? '—' : `${days}d`}</span>
      {!!desk.open_document_requests_count && (
        <Badge variant="warning" size="sm">
          {desk.open_document_requests_count} doc{desk.open_document_requests_count === 1 ? '' : 's'}
        </Badge>
      )}
    </div>
  );
}

function EmptyState() {
  const { user } = useAuthStore();
  const canCreateClaim = useCan('claims', 'create') && !isInsurerRole(user?.role);
  return (
    <div className="text-center py-12">
      {/* Was text-gray-400 on white: ~2.8:1, below the 4.5:1 floor. */}
      <div className="mx-auto h-12 w-12 text-gray-500 dark:text-gray-400">
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      </div>
      <h3 className="mt-4 text-sm font-semibold text-gray-900 dark:text-white">No claims found</h3>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        {canCreateClaim ? 'Get started by creating a new claim.' : 'No claims to display.'}
      </p>
      {canCreateClaim && (
        <div className="mt-6">
          <Link to="/claims/new">
            <Button leftIcon={<PlusIcon className="w-5 h-5" />}>New Claim</Button>
          </Link>
        </div>
      )}
    </div>
  );
}
